import { cn } from "@/lib/utils";

const SIZES = {
  xs: "h-6 w-6 text-[0.625rem]",
  sm: "h-8 w-8 text-[0.65rem]",
  md: "h-10 w-10 text-[0.75rem]",
  lg: "h-12 w-12 text-[0.9rem]",
  xl: "h-16 w-16 text-sm",
} as const;

export type ManagerAvatarSize = keyof typeof SIZES;

interface ManagerAvatarProps {
  name: string;
  size?: ManagerAvatarSize;
  className?: string;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function hash(str: string) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function isLight(background: string) {
  const match = /hsl\(\s*\d+\s+\d+(?:\.\d+)?%\s+(\d+(?:\.\d+)?)%\s*\)/.exec(background);
  if (!match) return false;
  return parseFloat(match[1]) > 55;
}

/**
 * Rights-safe manager identity avatar.
 * Renders the manager's initials in a deterministic, theme-aware color badge.
 * No third-party photographs are used.
 */
export function ManagerAvatar({ name, size = "md", className }: ManagerAvatarProps) {
  const letters = initials(name);
  const h = hash(name);
  const hue = h % 360;
  const background = `hsl(${hue} 58% 28%)`;
  const foreground = isLight(background) ? "#0F172A" : "#FFFFFF";

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-display font-bold tracking-tight ring-1 ring-white/10",
        SIZES[size],
        className
      )}
      style={{ backgroundColor: background, color: foreground }}
      aria-hidden
      title={name}
    >
      {letters}
    </span>
  );
}

export default ManagerAvatar;
